import { Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTicketFeaturesQuery } from '@/entities/ticket-feature'
import type { ITicketFeature } from '@/shared/api/types/ticket-feature-types'
import { useDashboardDisplayLanguage } from '@/shared/hooks/use-dashboard-display-language'
import { Button } from '@/shared/ui/button'
import { DataTableFilterField, DataTableToolbar } from '@/shared/ui/data-table'
import { Input } from '@/shared/ui/input'
import { PageContainer } from '@/shared/ui/page-container'
import { PageHeader } from '@/shared/ui/page-header'
import { TicketFeatureDrawer } from '@/features/ticket-feature/ticket-feature-drawer'
import { TicketFeatureListTable } from '@/features/ticket-feature/ticket-feature-list-table'

export function TicketFeatureListPage() {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [keywords, setKeywords] = useState('')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editingFeature, setEditingFeature] = useState<ITicketFeature | null>(null)

  const { activeLanguage } = useDashboardDisplayLanguage()

  const { data, isLoading } = useTicketFeaturesQuery({
    current: page,
    pageSize,
    ...(keywords ? { keywords } : {}),
  })

  const features = data?.data.data ?? []
  const total = data?.data.total ?? 0

  function handleEdit(feature: ITicketFeature) {
    setEditingFeature(feature)
    setDrawerOpen(true)
  }

  function handleCreate() {
    setEditingFeature(null)
    setDrawerOpen(true)
  }

  return (
    <PageContainer>
      <PageHeader
        title={t('ticketFeature.title', { defaultValue: 'Ticket Features' })}
        breadcrumbs={[{ label: t('ticketFeature.title', { defaultValue: 'Ticket Features' }) }]}
        actions={
          <Button onClick={handleCreate}>
            <Plus className="mr-1.5 h-4 w-4" />
            {t('ticketFeature.create', { defaultValue: 'Create Feature' })}
          </Button>
        }
      />

      <DataTableToolbar>
        <DataTableFilterField label={t('common.search')}>
          <Input
            className="w-64"
            placeholder={t('common.search')}
            value={keywords}
            onChange={(e) => {
              setKeywords(e.target.value)
              setPage(1)
            }}
          />
        </DataTableFilterField>
      </DataTableToolbar>

      <TicketFeatureListTable
        data={features}
        loading={isLoading}
        pagination={{
          current: page,
          pageSize,
          total,
          onChange: (nextPage, nextPageSize) => {
            setPage(nextPage)
            setPageSize(nextPageSize)
          },
        }}
        languageCode={activeLanguage}
        onEdit={handleEdit}
      />

      <TicketFeatureDrawer
        open={drawerOpen}
        feature={editingFeature}
        onOpenChange={setDrawerOpen}
      />
    </PageContainer>
  )
}
